import React, { useRef, useState } from 'react';
import {
    Heading2, AlignLeft, Image as ImageIcon, Video as VideoIcon,
    Trash2, ChevronUp, ChevronDown, Plus, Loader, Upload, CheckCircle
} from 'lucide-react';
import { ManualBlock, ManualBlockType } from '../types';
import { compressVideo, formatBytes } from '../utils/videoCompression';

export function genBlockId(): string {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export function parseBlocks(content?: string): ManualBlock[] {
    if (!content || content.trim() === '') return [];
    try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed)) {
            return parsed
                .filter(b => b && typeof b === 'object' && b.type)
                .map((b: any) => ({
                    id: b.id || genBlockId(),
                    type: b.type as ManualBlockType,
                    text: b.text || '',
                    url: b.url || ''
                }));
        }
    } catch {}
    return [];
}

export function serializeBlocks(blocks: ManualBlock[]): string {
    return JSON.stringify(blocks);
}

const BLOCK_META: Record<ManualBlockType, { label: string; icon: React.ReactNode; color: string }> = {
    heading:   { label: 'Heading',   icon: <Heading2 className="w-4 h-4"/>,    color: 'text-purple-600 bg-purple-50' },
    paragraph: { label: 'Paragraph', icon: <AlignLeft className="w-4 h-4"/>,   color: 'text-blue-600 bg-blue-50' },
    image:     { label: 'Image',     icon: <ImageIcon className="w-4 h-4"/>,   color: 'text-green-600 bg-green-50' },
    video:     { label: 'Video',     icon: <VideoIcon className="w-4 h-4"/>,   color: 'text-amber-600 bg-amber-50' },
};

interface Props {
    blocks: ManualBlock[];
    onChange: (next: ManualBlock[]) => void;
    uploadVideo: (blob: Blob, onProgress: (pct: number) => void) => Promise<string>;
    uploadImage: (file: File) => Promise<string>;
    onError: (msg: string) => void;
}

const ManualBlockEditor: React.FC<Props> = ({ blocks, onChange, uploadVideo, uploadImage, onError }) => {

    const update = (id: string, patch: Partial<ManualBlock>) => {
        onChange(blocks.map(b => b.id === id ? { ...b, ...patch } : b));
    };

    const remove = (id: string) => {
        onChange(blocks.filter(b => b.id !== id));
    };

    const move = (id: string, dir: -1 | 1) => {
        const idx = blocks.findIndex(b => b.id === id);
        const target = idx + dir;
        if (target < 0 || target >= blocks.length) return;
        const next = [...blocks];
        [next[idx], next[target]] = [next[target], next[idx]];
        onChange(next);
    };

    const add = (type: ManualBlockType) => {
        onChange([...blocks, { id: genBlockId(), type, text: '', url: '' }]);
    };

    return (
        <div className="space-y-3">
            {blocks.map((block, idx) => {
                const meta = BLOCK_META[block.type];
                return (
                    <div key={block.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
                            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md flex items-center gap-1.5 ${meta.color}`}>
                                {meta.icon} {meta.label}
                            </span>
                            <div className="flex items-center gap-1">
                                <button onClick={() => move(block.id, -1)} disabled={idx === 0} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-white rounded-md disabled:opacity-30" title="Move up">
                                    <ChevronUp className="w-4 h-4"/>
                                </button>
                                <button onClick={() => move(block.id, 1)} disabled={idx === blocks.length - 1} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-white rounded-md disabled:opacity-30" title="Move down">
                                    <ChevronDown className="w-4 h-4"/>
                                </button>
                                <button onClick={() => remove(block.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-white rounded-md" title="Delete">
                                    <Trash2 className="w-4 h-4"/>
                                </button>
                            </div>
                        </div>
                        <div className="p-4">
                            {block.type === 'heading' && (
                                <input
                                    type="text"
                                    value={block.text || ''}
                                    onChange={e => update(block.id, { text: e.target.value })}
                                    className="w-full p-3 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all font-serif font-bold text-xl"
                                    placeholder="Heading text..."
                                />
                            )}
                            {block.type === 'paragraph' && (
                                <textarea
                                    value={block.text || ''}
                                    onChange={e => update(block.id, { text: e.target.value })}
                                    rows={4}
                                    className="w-full p-3 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all leading-relaxed"
                                    placeholder="Paragraph text..."
                                />
                            )}
                            {block.type === 'image' && (
                                <ImageBlock
                                    url={block.url || ''}
                                    text={block.text || ''}
                                    onUrlChange={(url) => update(block.id, { url })}
                                    onTextChange={(text) => update(block.id, { text })}
                                    uploadImage={uploadImage}
                                    onError={onError}
                                />
                            )}
                            {block.type === 'video' && (
                                <VideoBlock
                                    url={block.url || ''}
                                    onUrlChange={(url) => update(block.id, { url })}
                                    uploadVideo={uploadVideo}
                                    onError={onError}
                                />
                            )}
                        </div>
                    </div>
                );
            })}

            <div className="flex flex-wrap gap-2 pt-2">
                {(Object.keys(BLOCK_META) as ManualBlockType[]).map(type => {
                    const meta = BLOCK_META[type];
                    return (
                        <button
                            key={type}
                            onClick={() => add(type)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all border-2 border-dashed border-gray-200 hover:border-current ${meta.color}`}
                        >
                            <Plus className="w-4 h-4"/> {meta.icon} {meta.label}
                        </button>
                    );
                })}
            </div>

            {blocks.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm italic">
                    No content yet — add a block above.
                </div>
            )}
        </div>
    );
};

// ============================================================
// IMAGE BLOCK
// ============================================================

interface ImageBlockProps {
    url: string;
    text: string;
    onUrlChange: (url: string) => void;
    onTextChange: (text: string) => void;
    uploadImage: (file: File) => Promise<string>;
    onError: (msg: string) => void;
}

const ImageBlock: React.FC<ImageBlockProps> = ({ url, text, onUrlChange, onTextChange, uploadImage, onError }) => {
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFile = async (f: File | undefined) => {
        if (!f) return;
        if (!f.type.startsWith('image/')) {
            onError('Please select an image file.');
            return;
        }
        setUploading(true);
        try {
            const newUrl = await uploadImage(f);
            if (newUrl) onUrlChange(newUrl);
        } catch (err: any) {
            onError(err?.message || 'Image upload failed.');
        } finally {
            setUploading(false);
        }
    };

    if (url) {
        return (
            <div className="space-y-3">
                <img src={url} alt={text || ''} className="w-full max-h-72 object-contain rounded-xl bg-gray-50 border border-gray-100"/>
                <input
                    type="text"
                    value={text}
                    onChange={e => onTextChange(e.target.value)}
                    className="w-full p-3 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                    placeholder="Caption (optional)"
                />
                <button onClick={() => onUrlChange('')} className="text-xs text-red-500 hover:text-red-700 font-bold flex items-center gap-1">
                    <Trash2 className="w-3 h-3"/> Remove image
                </button>
            </div>
        );
    }

    return (
        <div
            onClick={() => inputRef.current?.click()}
            className={`p-6 border-2 border-dashed rounded-xl text-center cursor-pointer transition-all ${uploading ? 'border-brand bg-brand/5' : 'border-gray-300 hover:border-brand hover:bg-brand/5'}`}
        >
            {uploading ? (
                <>
                    <Loader className="w-6 h-6 animate-spin text-brand mx-auto mb-2"/>
                    <p className="font-bold text-sm text-gray-700">Uploading…</p>
                </>
            ) : (
                <>
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2"/>
                    <p className="font-bold text-sm text-gray-700">Click to upload image</p>
                    <p className="text-xs text-gray-400 mt-1">Auto-converted to WebP</p>
                </>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploading}
                onChange={(e) => handleFile(e.target.files?.[0])}
            />
        </div>
    );
};

// ============================================================
// VIDEO BLOCK
// ============================================================

interface VideoBlockProps {
    url: string;
    onUrlChange: (url: string) => void;
    uploadVideo: (blob: Blob, onProgress: (pct: number) => void) => Promise<string>;
    onError: (msg: string) => void;
}

const VideoBlock: React.FC<VideoBlockProps> = ({ url, onUrlChange, uploadVideo, onError }) => {
    const [phase, setPhase] = useState<'idle' | 'compressing' | 'uploading' | 'done'>('idle');
    const [progress, setProgress] = useState(0);
    const [originalSize, setOriginalSize] = useState(0);
    const [compressedSize, setCompressedSize] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFile = async (f: File | undefined) => {
        if (!f) return;
        if (!f.type.startsWith('video/')) {
            onError('Please select a video file.');
            return;
        }
        setOriginalSize(f.size);
        try {
            setPhase('compressing');
            setProgress(0);
            const blob = await compressVideo(f, {
                onProgress: (pct) => setProgress(pct),
                onPhase: (p) => { if (p === 'loading') setPhase('compressing'); }
            });
            setCompressedSize(blob.size);

            setPhase('uploading');
            setProgress(0);
            const newUrl = await uploadVideo(blob, (pct) => setProgress(pct));
            if (newUrl) {
                onUrlChange(newUrl);
                setPhase('done');
                setTimeout(() => setPhase('idle'), 1500);
            } else {
                onError('Upload failed.');
                setPhase('idle');
            }
        } catch (err: any) {
            onError(err?.message || 'Video processing failed.');
            setPhase('idle');
        }
    };

    if (url && phase === 'idle') {
        return (
            <div className="space-y-2">
                <video src={url} controls playsInline className="w-full max-h-72 object-contain rounded-xl bg-black"/>
                <button onClick={() => onUrlChange('')} className="text-xs text-red-500 hover:text-red-700 font-bold flex items-center gap-1">
                    <Trash2 className="w-3 h-3"/> Remove video
                </button>
            </div>
        );
    }

    if (phase === 'compressing' || phase === 'uploading') {
        return (
            <div className="p-6 bg-brand/5 border-2 border-brand/30 rounded-xl text-center">
                <Loader className="w-6 h-6 animate-spin text-brand mx-auto mb-3"/>
                <p className="font-bold text-sm text-gray-800">
                    {phase === 'compressing' ? 'Compressing video…' : 'Uploading…'}
                </p>
                <div className="w-full bg-white rounded-full h-2 mt-3 overflow-hidden">
                    <div className="bg-brand h-full transition-all duration-200" style={{ width: `${progress}%` }}/>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    {progress}%
                    {phase === 'uploading' && compressedSize > 0 && ` — ${formatBytes(compressedSize)}`}
                </p>
                {phase === 'compressing' && originalSize > 0 && (
                    <p className="text-[10px] text-gray-400 mt-1">Original: {formatBytes(originalSize)}</p>
                )}
            </div>
        );
    }

    if (phase === 'done') {
        return (
            <div className="p-6 bg-green-50 border-2 border-green-200 rounded-xl text-center">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2"/>
                <p className="font-bold text-sm text-green-700">Video uploaded</p>
            </div>
        );
    }

    return (
        <div
            onClick={() => inputRef.current?.click()}
            className="p-6 border-2 border-dashed border-gray-300 hover:border-brand hover:bg-brand/5 rounded-xl text-center cursor-pointer transition-all"
        >
            <VideoIcon className="w-6 h-6 text-gray-400 mx-auto mb-2"/>
            <p className="font-bold text-sm text-gray-700">Click to upload video</p>
            <p className="text-xs text-gray-400 mt-1">Auto-compressed before upload</p>
            <input
                ref={inputRef}
                type="file"
                accept="video/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
            />
        </div>
    );
};

export default ManualBlockEditor;
